/**
 * Дашборд администратора.
 *
 * Главный экран отвечает на один вопрос: где сегодня болит.
 * Поэтому первое, что видно, — очередь приёмки: это ежедневная работа
 * админа. Графики и статистика вторичны и живут ниже.
 */
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Page, PageHeader } from '../components/Layout';
import { Empty, Loading, Person, Stat } from '../components/ui';
import { bytes, money, untilDeadline } from '../lib/format';
import type { Video } from '../lib/types';

type Dash = {
  inProgress: number;
  waitingReview: number;
  overdue: number;
  done: number;
  overdueList: Array<{
    id: string;
    status: string;
    workerId: string;
    editorId: string | null;
    mode: string;
    deadlineShootAt: string;
    editDeadlineAt: string | null;
    task: { number: number; title: string };
  }>;
  byDay: Array<{ date: string; count: number }>;
};

/**
 * Диаграмма принятых видео по дням.
 *
 * Нарисована руками на обычных div вместо библиотеки графиков: тридцать
 * столбиков не стоят трёхсот килобайт в бандле, которые пришлось бы
 * качать каждому при каждом заходе.
 *
 * Считаем от приёмки, а не от загрузки: работа становится работой
 * в тот момент, когда её приняли.
 */
function MonthChart({ data }: { data: Array<{ date: string; count: number }> }) {
  const max = Math.max(1, ...data.map((d) => d.count));
  /** Высота области под столбики в пикселях. Над ними ещё строка с числом. */
  const BAR_AREA = 130;
  const total = data.reduce((s, d) => s + d.count, 0);
  const busiest = data.reduce((a, b) => (b.count > a.count ? b : a), data[0]);

  const label = (iso: string) =>
    new Date(iso + 'T12:00:00').toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'short',
    });

  return (
    <div className="card">
      <div className="flex items-baseline gap-2 mb-1">
        <span className="mono text-2xl font-extrabold">{total}</span>
        <span className="text-[13px] text-muted">видео принято за 30 дней</span>
      </div>
      <div className="text-[11.5px] text-muted2 mb-4">
        {busiest && busiest.count > 0
          ? `лучший день — ${label(busiest.date)}, ${busiest.count} шт.`
          : 'пока ничего не принято'}
      </div>

      {/* Столбики с числом над каждым.
          Высота считается от максимума, а не от абсолютного значения:
          при трёх видео в день график иначе выглядел бы пустым.
          Число рисуем прямо над столбиком — наводиться мышкой на тридцать
          штук неудобно, а цифра нужна сразу. */}
      <div className="flex items-end gap-[3px]">
        {data.map((d) => (
          <div key={d.date} className="flex-1 min-w-0 flex flex-col items-center">
            <span
              className={`mono text-[11px] leading-none mb-1 ${
                d.count > 0 ? 'text-muted' : 'text-transparent'
              }`}
            >
              {d.count || 0}
            </span>
            <div
              className={`w-full rounded-sm transition-colors ${
                d.count > 0 ? 'bg-accent hover:bg-accent2' : 'bg-[#1A1A24]'
              }`}
              style={{ height: d.count > 0 ? Math.max(6, (d.count / max) * BAR_AREA) : 3 }}
              title={`${label(d.date)} · ${d.count}`}
            />
          </div>
        ))}
      </div>

      {/* Подписи не у каждого дня — тридцать дат в ряд слиплись бы
          в нечитаемую кашу. Каждая пятая даёт достаточно опорных точек */}
      <div className="flex gap-[3px] mt-2">
        {data.map((d, i) => (
          <div key={d.date} className="flex-1 min-w-0 text-center">
            {i % 5 === 0 || i === data.length - 1 ? (
              <span className="text-[10.5px] text-muted2 whitespace-nowrap">{label(d.date)}</span>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

type Balances = { totalToPay: number; users: Array<{ id: string; balance: number }> };
type Usage = { totalBytes: number; pinned: { bytes: number } };

export function DashboardPage() {
  const navigate = useNavigate();

  const { data: stats, isLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api.get<Dash>('/api/videos/dashboard'),
    refetchInterval: 60_000,
  });

  const { data: review } = useQuery({
    queryKey: ['review-queue'],
    queryFn: () => api.get<Video[]>('/api/videos/review'),
  });

  const { data: balances } = useQuery({
    queryKey: ['balances'],
    queryFn: () => api.get<Balances>('/api/finance/balances'),
  });

  const { data: usage } = useQuery({
    queryKey: ['storage-usage'],
    queryFn: () => api.get<Usage>('/api/storage/usage'),
  });

  return (
    <>
      <PageHeader
        title="Дашборд"
        sub={new Date().toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })}
        actions={
          <button className="btn btn-primary" onClick={() => navigate('/tasks/new')}>
            + Создать ТЗ
          </button>
        }
      />
      <Page>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat value={stats?.inProgress ?? '—'} label="в работе" />
          <Stat value={stats?.waitingReview ?? '—'} label="ждут приёмки" tone="warn" />
          <Stat
            value={stats?.overdue ?? '—'}
            label="просрочено"
            tone={stats?.overdue ? 'dang' : undefined}
          />
          <Stat
            value={balances ? money(balances.totalToPay).replace(' ₽', '') : '—'}
            label="к выплате, ₽"
            tone="ok"
          />
        </div>

        <div className="sec">Сделано за месяц</div>
        {stats?.byDay ? <MonthChart data={stats.byDay} /> : <Loading rows={1} />}

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-5 mt-6 items-start">
          <div>
            <div className="sec">
              Очередь приёмки{review?.length ? ` · ${review.length}` : ''}
            </div>

            {isLoading ? (
              <Loading />
            ) : review?.length ? (
              <div className="space-y-3">
                {review.map((v) => (
                  <Link
                    key={v.id}
                    to={`/review/${v.id}`}
                    className="card flex gap-4 items-center hover:border-line2 hover:bg-surface2 transition-colors"
                  >
                    {/* Кадр из видео система делает сама при загрузке.
                        Раньше он лежал в хранилище неиспользованным, и все
                        карточки выглядели одинаковыми серыми прямоугольниками.
                        Треугольник оставлен поверх: он говорит, что это видео
                        и его можно открыть, а лёгкое затемнение под ним нужно,
                        чтобы он читался и на светлом кадре */}
                    <div className="relative w-[94px] h-[54px] shrink-0 rounded-[9px] overflow-hidden">
                      {v.thumbUrl ? (
                        <img
                          src={v.thumbUrl}
                          alt=""
                          loading="lazy"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-[#3E2C63] to-[#1E2745]" />
                      )}
                      <div className="absolute inset-0 grid place-items-center bg-black/25 text-white/90">
                        ▶
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm">{v.task.title}</div>
                      <div className="text-[11.5px] text-muted2 mt-0.5">
                        видео {v.index} · сдал{' '}
                        {v.editor?.displayName ?? v.worker.displayName}
                        {v.isOverdue && <span className="text-dang"> · просрочено</span>}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="mono font-bold">{money(v.priceShoot + v.priceEdit)}</div>
                      <div className="btn btn-sm btn-primary mt-1.5 inline-block">Смотреть</div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="card">
                <Empty title="Очередь пуста" hint="Всё принято" />
              </div>
            )}
          </div>

          <div>
            {/* Блок появляется только когда есть о чём говорить.
                Про отсутствие просрочек и так сообщает счётчик наверху,
                а вечно пустая карточка занимала место зря */}
            {stats?.overdueList.length ? (
              <>
              <div className="sec">Кто тормозит</div>
              <div className="space-y-3">
                {stats.overdueList.slice(0, 6).map((v) => {
                  const deadline = v.editDeadlineAt ?? v.deadlineShootAt;
                  const { text } = untilDeadline(deadline);
                  const isEdit = ['AWAITING_EDIT', 'EDITING', 'REVISION'].includes(v.status);
                  const ownerId = isEdit ? (v.editorId ?? v.workerId) : v.workerId;
                  return (
                    <Link
                      key={v.id}
                      to={`/videos/${v.id}`}
                      className="card flex items-center gap-3 hover:border-line2 transition-colors"
                    >
                      <Person id={ownerId} name={isEdit ? 'Монтаж' : 'Съёмка'} size={30} />
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-[13.5px] truncate">{v.task.title}</div>
                        <div className="text-[11.5px] text-dang">{text}</div>
                      </div>
                    </Link>
                  );
                })}
              </div>
              </>
            ) : null}

            <div className="sec !mt-0">Хранилище</div>
            <div className="card">
              <div className="flex justify-between items-baseline">
                <span className="text-muted text-[13px]">Занято</span>
                <span className="mono font-bold text-[17px]">
                  {usage ? bytes(usage.totalBytes) : '—'}
                </span>
              </div>
              <div className="text-[11.5px] text-muted2 mt-2 leading-relaxed">
                Закреплено навсегда: {usage ? bytes(usage.pinned.bytes) : '—'}
              </div>
              <Link to="/storage" className="btn btn-sm w-full mt-3 block text-center">
                Открыть
              </Link>
            </div>
          </div>
        </div>
      </Page>
    </>
  );
}
